import { QueryKey, useMutation, UseQueryResult } from '@tanstack/react-query'
import { MissionsFilters, FiltersResourcesEnum } from '../store/filters'
import useStore from '../store'
import {
  fetch,
  create,
  update,
  MissionUpdatePayload,
  Mission,
  MissionCreationPayload,
  MissionWithSummary,
  get,
  exportSummary,
  fetchSummary,
  SummaryFilters,
  ExportSummaryParams,
  WeeksSummaryParams,
  fetchWeeksSummary,
  WeeksSummary,
  count,
  unAbandon,
  UnAbandonMissionPayload,
  fetchWeeksSummaryStatus,
  WeeksSummaryStatus,
} from '../api/missions'
import { queryClient, QueryOptions, UseMutationResult, useResource, useResources } from '.'
import { WeekSummary } from '../../../backend/src/services/resources/missions/missions.summary.service.types'
import { invalidateWorkPeriodsQueries } from './work-periods'
import { useNormalizeEventsFilters } from '../hooks/use-normalize-events-filters'
import { getWeekdayMondayBased } from '../utils/date'
import { getDay } from 'date-fns'

//Queries
export const useMissionQuery = (id?: string): UseQueryResult<MissionWithSummary, any> =>
  useResource('missions', get, id)

export const useMissionsCountQuery = (
  filters?: MissionsFilters,
  options?: QueryOptions,
): UseQueryResult<number, any> =>
  useResources<number, MissionsFilters>(
    'missions/count',
    count,
    filters || useStore(state => state.filters[FiltersResourcesEnum.missions]),
    options,
  )

export const useMissionsQuery = (
  filters?: Partial<MissionsFilters> & { populate?: string[] },
  options?: QueryOptions,
): UseQueryResult<MissionWithSummary[], any> =>
  useResources<MissionWithSummary[], Partial<MissionsFilters>>(
    'missions',
    fetch,
    filters || useStore(state => state.filters[FiltersResourcesEnum.missions]),
    options,
  )

export const useSummaryQuery = (
  filters?: Partial<SummaryFilters>,
  options?: QueryOptions,
): UseQueryResult<WeekSummary, any> => {
  const allFilters = filters || useStore(state => state.filters[FiltersResourcesEnum.missions])

  // Extract and exclude summaryMinimalMode and displayDay from filters
  // These are UI-specific and should not be sent to the API
  const { summaryMinimalMode, displayDay, ...params } = allFilters

  // Normalize events filters (transform sentinels to actual event IDs)
  const normalizedEvents = useNormalizeEventsFilters(params?.events)

  // Merge normalized events into params
  const normalizedParams = {
    ...params,
    ...normalizedEvents,
    days: displayDay ? [getWeekdayMondayBased(getDay(new Date(displayDay)))] : undefined,
  }

  // Explicitly reference excluded properties to satisfy linter
  void summaryMinimalMode
  void displayDay

  return useResources<WeekSummary, Partial<SummaryFilters>>(
    'missions/summary',
    fetchSummary,
    normalizedParams,
    options,
  )
}

export const useWeeksSummaryQuery = (
  filters: WeeksSummaryParams,
  options?: QueryOptions,
): UseQueryResult<WeeksSummary, any> =>
  useResources<WeeksSummary, WeeksSummaryParams>(
    'missions/weeks-summary',
    fetchWeeksSummary,
    filters,
    options,
  )

export const useWeeksSummaryStatusQuery = (
  filters: WeeksSummaryParams,
  options?: QueryOptions,
): UseQueryResult<WeeksSummaryStatus, any> =>
  useResources<WeeksSummaryStatus, WeeksSummaryParams>(
    'missions/weeks-summary/status',
    fetchWeeksSummaryStatus,
    filters,
    options,
  )

//Mutations
const useCreateMutationKey = (): QueryKey => ['missions', 'create']
export const useCreateMutation = (): UseMutationResult<Mission, MissionCreationPayload, any> =>
  useMutation(create, {
    mutationKey: useCreateMutationKey(),
    onSuccess: () => {
      invalidateMissionsQueries()
      invalidateWorkPeriodsQueries()
    },
  })

export const useUpdateMutationKey = (): QueryKey => ['missions', 'update']
export const useUpdateMutation = (): UseMutationResult<Mission, MissionUpdatePayload, any> =>
  useMutation(update, {
    mutationKey: useUpdateMutationKey(),
    onSuccess: () => {
      invalidateMissionsQueries()
      invalidateWorkPeriodsQueries()
    },
  })

const useExportSummaryMutationKey = (): QueryKey => ['missions', 'csv']
export const useExportSummaryMutation = (): UseMutationResult<string, ExportSummaryParams, any> =>
  useMutation(exportSummary, {
    mutationKey: useExportSummaryMutationKey(),
  })

export const useUnAbandonMutationKey = (): QueryKey => ['missions', 'unAbandon']
export const useUnAbandonMutation = (): UseMutationResult<Mission, UnAbandonMissionPayload, any> =>
  useMutation(unAbandon, {
    mutationKey: useUnAbandonMutationKey(),
    onSuccess: () => {
      invalidateMissionsQueries()
      invalidateWorkPeriodsQueries()
    },
  })

//Methods

export const invalidateMissionsQueries = (): void => {
  queryClient.invalidateQueries(['missions'])
  queryClient.invalidateQueries(['missions/summary'])
}
